import { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import { API_CONFIG, ADMIN_ROLES } from '../config/authConfig';
import { LoginCredentials, AuthResponse } from '../types/authTypes';
import { clearUserSession } from '../utils/sessionManager';

interface PendingLoginData {
  credentials: LoginCredentials;
  response: AuthResponse;
  rememberMe: boolean;
}

// Roles that require password expiration check
const ROLES_REQUIRING_PASSWORD_CHECK = ['user', 'storage'];

// Check if password needs to be changed (null or older than 30 days)
// Only applies to user and storage roles
const isPasswordExpired = (lastPassUpd: string | null | undefined, role: string): boolean => {
  // Only check for user and storage roles
  if (!ROLES_REQUIRING_PASSWORD_CHECK.includes(role)) {
    return false;
  }

  if (!lastPassUpd) return true;

  const lastUpdate = new Date(lastPassUpd);
  const now = new Date();
  const diffDays = Math.floor((now.getTime() - lastUpdate.getTime()) / (1000 * 60 * 60 * 24));

  return diffDays > 30;
};

export const useStandardAuth = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [requiresPasswordChange, setRequiresPasswordChange] = useState<boolean>(false);
  const [pendingLoginData, setPendingLoginData] = useState<PendingLoginData | null>(null);
  const navigate = useNavigate();

  const completeLogin = (credentials: LoginCredentials, response: AuthResponse, rememberMe: boolean) => {
    // Clear any leftover data from a previous session
    clearUserSession();

    // Manage cookies for "remember me"
    if (rememberMe) {
      Cookies.set('rememberedLogin', credentials.login, { expires: 1000 });
    } else {
      Cookies.remove('rememberedLogin');
    }

    // Save user data
    localStorage.setItem('userId', response.userId);
    localStorage.setItem('role', response.role);
    localStorage.setItem('username', credentials.login);
    localStorage.setItem('guid', response.guid);
    localStorage.setItem('belongsStorage', response.belongsStorage);
    localStorage.setItem('cityId', response.cityId);

    // Navigate based on role
    if ((ADMIN_ROLES as readonly string[]).includes(response.role)) {
      navigate('/admin');
    } else {
      navigate('/dashboard');
    }
  };

  const handleLogin = async (credentials: LoginCredentials, rememberMe: boolean) => {
    setLoading(true);
    setError(null);

    const clientVersion = process.env.REACT_APP_VERSION || '1.0.3';

    try {
      const response = await axios.post<AuthResponse>(
        `${API_CONFIG.BASE_URL}/login`,
        {
          login: credentials.login,
          password: credentials.password,
          version: clientVersion
        }
      );

      if (response.data.success) {
        // Check if password change is required
        if (isPasswordExpired(response.data.lastPassUpd, response.data.role)) {
          setPendingLoginData({
            credentials,
            response: response.data,
            rememberMe
          });
          setRequiresPasswordChange(true);
          setLoading(false);
          return { success: true, requiresPasswordChange: true };
        }

        // Complete login immediately if password is not expired
        completeLogin(credentials, response.data, rememberMe);
        return { success: true };
      } else {
        const errorMessage = response.data.message || 'Неправильный логин или пароль';
        setError(errorMessage);

        // Если версия клиента не совпадает с сервером — очищаем кэш и перезагружаем страницу
        if (response.data.versionMismatch) {
          if ('caches' in window) {
            caches.keys().then((names) => {
              for (let name of names) {
                caches.delete(name);
              }
            });
          }
          setTimeout(() => {
            window.location.reload();
          }, 2000);
          return { success: false, error: errorMessage, versionMismatch: true };
        }

        return { success: false, error: errorMessage };
      }
    } catch (err) {
      console.error('Ошибка при авторизации:', err);
      const errorMessage = 'Ошибка подключения к серверу. Проверьте подключение и перезагрузите страницу через 5 минут.';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const onPasswordChangeComplete = () => {
    if (pendingLoginData) {
      localStorage.setItem('forceProfileCheck', 'true');

      completeLogin(
        pendingLoginData.credentials,
        pendingLoginData.response,
        pendingLoginData.rememberMe
      );
      setPendingLoginData(null);
      setRequiresPasswordChange(false);
    }
  };

  const clearError = () => setError(null);

  return {
    loading,
    error,
    handleLogin,
    clearError,
    requiresPasswordChange,
    pendingLoginData,
    onPasswordChangeComplete
  };
};
